import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, BarChart3, TrendingUp, Calendar, RefreshCw, User as UserIcon, Wallet, Receipt } from "lucide-react";
import { authHeaders } from "./auth";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api`;

const PERIODS = [
  { id: "month", label: "This Month" },
  { id: "year", label: "This Year" },
  { id: "all", label: "All Time" },
];

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function formatINR(n) {
  if (n == null || isNaN(n)) return "—";
  // Indian numbering — ₹1,23,456.78
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

export default function Analytics() {
  const [usage, setUsage] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [spend, setSpend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [period, setPeriod] = useState("month");
  const [windowMonths, setWindowMonths] = useState(6);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [u, m, s] = await Promise.all([
        fetch(`${API}/analytics/usage`, { headers: authHeaders() }).then((r) => r.json()),
        fetch(`${API}/analytics/monthly?months=${windowMonths}`, { headers: authHeaders() }).then((r) => r.json()),
        fetch(`${API}/analytics/spend`, { headers: authHeaders() }).then((r) => r.json()),
      ]);
      setUsage(u);
      setMonthly(m);
      setSpend(s);
    } catch {
      setErr("Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  }, [windowMonths]);

  useEffect(() => {
    load();
  }, [load]);

  const total = usage ? usage.totals[period] : 0;
  const sorted = useMemo(() => {
    if (!usage) return [];
    const arr = [...usage.users];
    arr.sort((a, b) => (b[period] || 0) - (a[period] || 0));
    return arr;
  }, [usage, period]);

  const maxBar = useMemo(() => {
    if (!monthly?.series?.length) return 1;
    return Math.max(1, ...monthly.series.map((s) => s.total));
  }, [monthly]);

  const periodLabel =
    period === "month"
      ? new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" })
      : period === "year"
        ? `${new Date().getFullYear()}`
        : "All Time";

  return (
    <div data-testid="analytics-view">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-full border border-heda-line bg-white p-1">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              data-testid={`period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={[
                "rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition",
                period === p.id ? "bg-heda-ink text-white" : "text-heda-mute hover:text-heda-ink",
              ].join(" ")}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          data-testid="analytics-refresh"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-full border border-heda-line bg-white px-3 py-1.5 text-[12px] text-heda-ink hover:border-heda-gold"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {err && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>
      )}

      {/* KPIs */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <KPI
          label={`Total renders · ${periodLabel}`}
          value={loading ? "…" : total.toLocaleString()}
          icon={<BarChart3 size={16} />}
          accent="gold"
          testId="kpi-total"
        />
        <KPI
          label="Active staff"
          value={loading ? "…" : sorted.filter((u) => (u[period] || 0) > 0).length}
          icon={<UserIcon size={16} />}
          accent="olive"
          testId="kpi-active"
        />
        <KPI
          label="Avg per active staff"
          value={
            loading
              ? "…"
              : (() => {
                  const active = sorted.filter((u) => (u[period] || 0) > 0).length;
                  return active ? (total / active).toFixed(1) : "0";
                })()
          }
          icon={<TrendingUp size={16} />}
          accent="ink"
          testId="kpi-avg"
        />
      </div>

      {/* Per-staff table */}
      <div className="mb-6 overflow-hidden rounded-2xl border border-heda-line bg-white">
        <div className="flex items-center justify-between border-b border-heda-line px-5 py-3">
          <div>
            <h3 className="font-display text-[15px] font-bold text-heda-ink">Per-staff breakdown</h3>
            <p className="text-[11.5px] text-heda-mute">{periodLabel}</p>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={22} className="animate-spin text-heda-gold" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-heda-mute">No usage yet.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-heda-cream/60 text-[11px] uppercase tracking-wider text-heda-mute">
              <tr>
                <th className="px-4 py-2.5">Staff</th>
                <th className="px-4 py-2.5 text-right hidden sm:table-cell">This Month</th>
                <th className="px-4 py-2.5 text-right hidden md:table-cell">This Year</th>
                <th className="px-4 py-2.5 text-right">All Time</th>
                <th className="px-4 py-2.5 text-right hidden md:table-cell">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((u) => (
                <tr key={u.user_id} data-testid={`staff-row-${u.user_id}`} className="border-t border-heda-line">
                  <td className="px-4 py-3">
                    <div className="font-medium text-heda-ink">{u.user_name}</div>
                    <div className="text-[11px] text-heda-mute">{u.user_email}</div>
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums hidden sm:table-cell ${period === "month" ? "font-bold text-heda-gold" : "text-heda-ink/80"}`}>
                    {u.month.toLocaleString()}
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums hidden md:table-cell ${period === "year" ? "font-bold text-heda-gold" : "text-heda-ink/80"}`}>
                    {u.year.toLocaleString()}
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums ${period === "all" ? "font-bold text-heda-gold" : "text-heda-ink/80"}`}>
                    {u.all.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-[11px] text-heda-mute hidden md:table-cell">
                    {formatRelative(u.last_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Spend overview — Indian rupees */}
      <div data-testid="spend-overview" className="mb-6 overflow-hidden rounded-2xl border border-heda-line bg-white">
        <div className="flex items-center justify-between border-b border-heda-line px-5 py-3">
          <div>
            <h3 className="font-display text-[15px] font-bold text-heda-ink">Spend overview</h3>
            <p className="text-[11.5px] text-heda-mute">
              Estimated AI cost in Indian Rupees ·{" "}
              {spend
                ? `${formatINR(spend.cost_per_render_inr)} per render @ ₹${spend.fx_rate_inr_per_usd}/USD`
                : "—"}
            </p>
            {spend?.tracking_since && (
              <p
                data-testid="spend-tracking-since"
                className="mt-0.5 text-[11.5px] text-heda-mute"
              >
                Real model-call metering since {spend.tracking_since}. Earlier spend is not counted.
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
          <SpendCard
            label="This month"
            renders={spend?.this_month?.renders}
            spendInr={spend?.this_month?.spend_inr}
            highlight
            testId="spend-month"
          />
          <SpendCard
            label="This year"
            renders={spend?.this_year?.renders}
            spendInr={spend?.this_year?.spend_inr}
            testId="spend-year"
          />
          <SpendCard
            label="All time"
            renders={spend?.all_time?.renders}
            spendInr={spend?.all_time?.spend_inr}
            testId="spend-all"
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-heda-line bg-heda-cream/40 px-5 py-2 text-[11px] text-heda-mute">
          <span className="inline-flex items-center gap-1">
            <Receipt size={11} /> Avg per render:{" "}
            <span className="font-bold text-heda-ink">
              {spend ? formatINR(spend.avg_cost_per_render_inr) : "—"}
            </span>
          </span>
          <span className="inline-flex items-center gap-1">
            <Wallet size={11} /> FX rate:{" "}
            <span className="font-bold text-heda-ink">
              ₹{spend?.fx_rate_inr_per_usd ?? "—"}/USD
            </span>
          </span>
          <span className="text-heda-mute/80">
            Per-render cost is configurable in backend env (RENDER_COST_USD, USD_TO_INR).
          </span>
        </div>
      </div>

      {/* Monthly trend chart */}
      <div className="overflow-hidden rounded-2xl border border-heda-line bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-heda-line px-5 py-3">
          <div>
            <h3 className="font-display text-[15px] font-bold text-heda-ink">Monthly trend</h3>
            <p className="text-[11.5px] text-heda-mute">Renders &amp; spend per month — hover a bar for details</p>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-heda-line bg-heda-cream/40 p-0.5">
            {[3, 6, 12].map((n) => (
              <button
                key={n}
                data-testid={`chart-window-${n}`}
                onClick={() => setWindowMonths(n)}
                className={[
                  "rounded-full px-2.5 py-1 text-[11px] font-semibold transition",
                  windowMonths === n ? "bg-heda-ink text-white" : "text-heda-mute hover:text-heda-ink",
                ].join(" ")}
              >
                {n}M
              </button>
            ))}
          </div>
        </div>
        <div className="px-4 pb-4 pt-8 sm:px-5">
          {loading ? (
            <div className="flex h-44 items-center justify-center">
              <Loader2 size={22} className="animate-spin text-heda-gold" />
            </div>
          ) : !monthly?.series?.length ? (
            <div className="py-10 text-center text-sm text-heda-mute">No data yet.</div>
          ) : (
            <div data-testid="chart-bars" className="flex flex-col">
              <div className="flex h-44 items-end gap-1.5 sm:gap-2">
                {monthly.series.map((s, i) => {
                  const h = s.total > 0 ? Math.max(4, Math.round((s.total / maxBar) * 100)) : 2;
                  const isCurrent = s.year === usage?.current_year && s.month === usage?.current_month;
                  const bgColor = isCurrent
                    ? "#B8860B"
                    : s.total > 0
                      ? "#8C9A5F"
                      : "#E8E1D0";
                  const inrPerRender = spend?.cost_per_render_inr || 0;
                  const monthInr = (s.total || 0) * inrPerRender;
                  return (
                    <div
                      key={`${s.year}-${s.month}`}
                      data-testid={`bar-${i}`}
                      className="group relative flex h-full min-w-0 flex-1 flex-col justify-end"
                      title={`${s.label}: ${s.total} renders · ${formatINR(monthInr)}`}
                    >
                      <div
                        style={{ height: `${h}%`, backgroundColor: bgColor }}
                        className="relative w-full rounded-t-md transition-all duration-300 hover:brightness-110"
                      >
                        {s.total > 0 && (
                          <div className="pointer-events-none absolute -top-7 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center whitespace-nowrap opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                            <span className="rounded-md bg-heda-ink px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-white shadow-sm">
                              {s.total.toLocaleString()} · {formatINR(monthInr)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex gap-1.5 sm:gap-2">
                {monthly.series.map((s) => (
                  <div
                    key={`lbl-${s.year}-${s.month}`}
                    className="flex min-w-0 flex-1 justify-center truncate text-[10px] uppercase tracking-wider text-heda-mute"
                  >
                    {s.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-heda-mute">
        <Calendar size={11} /> Counts are stored permanently — they survive the 7-day image cleanup.
      </p>
    </div>
  );
}

function KPI({ label, value, icon, accent, testId }) {
  const accents = {
    gold: "text-heda-gold bg-heda-gold/10 border-heda-gold/30",
    olive: "text-heda-olive bg-heda-olive-tint/60 border-heda-olive/30",
    ink: "text-heda-ink bg-white border-heda-line",
  };
  return (
    <div data-testid={testId} className="rounded-2xl border border-heda-line bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-heda-mute">{label}</span>
        <span className={["flex h-8 w-8 items-center justify-center rounded-full border", accents[accent]].join(" ")}>
          {icon}
        </span>
      </div>
      <div className="mt-2 font-display text-3xl font-bold tabular-nums text-heda-ink">{value}</div>
    </div>
  );
}

function SpendCard({ label, renders, spendInr, highlight, testId }) {
  return (
    <div
      data-testid={testId}
      className={[
        "rounded-xl border p-4",
        highlight
          ? "border-heda-gold/40 bg-heda-gold-tint/40"
          : "border-heda-line bg-heda-cream/40",
      ].join(" ")}
    >
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-heda-mute">
        {label}
      </div>
      <div className="mt-1.5 font-display text-2xl font-bold tabular-nums text-heda-ink">
        {spendInr == null ? "—" : formatINR(spendInr)}
      </div>
      <div className="mt-0.5 text-[11.5px] text-heda-mute">
        {renders == null ? "—" : `${renders.toLocaleString()} render${renders === 1 ? "" : "s"}`}
      </div>
    </div>
  );
}
